import React, { useState, useEffect } from "react";
import CustomMediumChip from "./CustomMediumChip";
import {Box, MenuItem} from '@mui/material';
import {POSITION_MODEL} from "../Helpers/PositionModel";
import {VisitorSearchTitle} from "./buttons/IconButtons";
import {useTranslation} from "react-i18next";
import CustomUserChip from "./CustomUserChip";
import {getFieldTypeById} from "../pages/Dashboard/Visitor/Type/Model/Model";
import {CustomSelectAndChildren} from "./CustomSelectAndChildren";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * CustomSelectViewCard Component
 *
 * Renders a dropdown select input and shows the selected option(s) using visual chips.
 * If the selected item contains associated fields, they are displayed as structured content under each chip.
 *
 * @param {Object} props - Component props
 * @param {string} props.id - The ID of the select input.
 * @param {Array<Object>} props.list - The list of options to be displayed.
 * @param {string} props.value - The currently selected value (ID of selected item).
 * @param {string} props.name - Name attribute for the select input.
 * @param {string} props.trans - Translation key prefix.
 * @param {boolean} props.required - Whether the field is required.
 * @param {function} props.onChange - Callback to handle value change. Receives a string ID.
 * @param {string} [props.className] - Optional CSS class for styling.
 * @param {boolean} [props.showNoSelectionOption] - Whether to include a 'no selection' option.
 * @param {boolean} [props.emptyFields] - Whether to highlight empty required fields.
 * @param {boolean} [props.disabled] - Whether the select input should be disabled.
 *
 * @returns {JSX.Element} A custom dropdown with chip card view for selected values.
 */

const CustomSelectViewCard = ({
                                  id,
                                  list,
                                  value, // string
                                  name,
                                  trans,
                                  required,
                                  onChange, // function(string)
                                  className,
                                  showNoSelectionOption,
                                  emptyFields,
                                  disabled,
                              }) => {
    const [selectedList, setSelectedList] = useState([]);
    const {t} = useTranslation();
    // Set selected item as chip if value matches a valid item in the list
    useEffect(() => {
        if (value) {
            const selected = list.find((item) => item?.id?.toLowerCase() === value.toLowerCase());
            if (selected) {
                setSelectedList([selected]);
            } else {
                setSelectedList([]);
            }
        } else {
            setSelectedList([]);
        }
    }, [value, list]);

    /**
     * Handle removal of a selected item (chip)
     * @param {Object} element - The item to remove from selection.
     */
    const handleDelete = (element) => {
        const filtered = selectedList.filter((e) => e.id !== element?.id);
        setSelectedList(filtered);

        if (value === element?.id && onChange) {
            onChange({ target: { name, value: "" } });
        }
    };

    return (
        <>
            <CustomSelectAndChildren
                id={id}
                value={value}
                name={name}
                trans={trans}
                required={required}
                onChange={onChange}
                className={className}
                showNoSelectionOption={showNoSelectionOption}
                emptyFields={emptyFields}
                disabled={disabled}
            >
                {list?.map((item) => (
                    <MenuItem key={item?.id} value={item?.id}>
                        {item.name}
                    </MenuItem>
                ))}
            </CustomSelectAndChildren>
            {selectedList?.length > 0 && (
                <div className="d-flex gap-4 input-padding-bottom flex-wrap">
                    {selectedList.map((element) => (
                        <CustomMediumChip
                            key={element.id}
                            title={element.name}
                            subtitle={`Fields: ${element.fields?.length ?? 0}`}
                            unit=""
                            completeWidth={true}
                            showAvatar={true}
                            Icon={VisitorSearchTitle}
                            iconPosition={POSITION_MODEL.LEFT}
                            onClickClose={() => handleDelete(element)}
                            children={element?.fields?.length > 0 && (
                                    <div className='mt-2 d-flex w-100 flex-column'>
                                        { element?.fields.map((item) => {
                                            const fieldTypeData = getFieldTypeById(item.fieldType);
                                            return (
                                                <div className="row">
                                                    <React.Fragment key={item.name}>
                                                        <Box className="col-3 w-100 my-1">
                                                            {item?.fieldName}
                                                        </Box>
                                                        <Box className="col-9 d-flex justify-content-start my-1">
                                                            <CustomUserChip
                                                                className="mt-0 chip-secondary-150-100"
                                                                name={fieldTypeData?.name}
                                                                Icon={fieldTypeData?.icon}
                                                                showAvatar={true}
                                                                variant="compact"
                                                                iconPosition={POSITION_MODEL.LEFT}
                                                                classNameIcon={"icon-color-secondary-700"}
                                                            />

                                                            <CustomUserChip
                                                                className="mt-0 chip-shades-150-200 ibl-font-w-500"
                                                                name={item.required ? t('required') : t('optional')}
                                                                showAvatar={false}
                                                                variant="compact"
                                                                iconPosition={POSITION_MODEL.LEFT}
                                                            />
                                                        </Box>
                                                    </React.Fragment>
                                                </div>
                                            );
                                        }) }
                                    </div>
                                )
                            }
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export default CustomSelectViewCard;
